// TODO consolidate w/ shop.ts
import { URLS } from '@/constants';
import {
  BulkAddCartItemsResponse,
  CartCheckout,
  CartItem,
  CartOrderDetails,
  DeleteCartItemRequest,
  UpdateCartItemRequest,
  UpdateCartItemResponse,
  UpdateCartOrderDetailsRequest,
} from '@/models/cart';
import { PromoCode } from '@/models/company';
import session from '@/services/session';

export async function getCart(promoCodes: { orderId: string; promoCodeId: number }[]) {
  const response = await session.get(`${URLS.CARTS.DEFAULT}/`, {
    params: {
      promo_codes: JSON.stringify(
        promoCodes.map((promocode) => {
          return {
            order_id: promocode.orderId,
            promo_code_id: promocode.promoCodeId,
          };
        }),
      ),
    },
  });

  return response.data;
}

/**
 * Gets number of items currently in cart
 */
export async function getCartStats() {
  const response = await session.get<{ count: number }>(`${URLS.CARTS.STATS}/`);

  return response.data;
}

/**
 * Clear cart at store or seller level.
 * @param {CartCheckout} cartInfo  object with buyerId and sellerId
 * @param {CartOrderDetails[]} orderDetails order details
 */
export async function checkoutCart({
  cartInfo,
  orderDetails,
}: {
  cartInfo: CartCheckout;
  orderDetails: CartOrderDetails[];
}) {
  const { buyerId, sellerId } = cartInfo;
  const response = await session.post(`${URLS.CARTS.CHECKOUT}/`, {
    buyer_id: buyerId,
    seller_id: sellerId,
    order_details: orderDetails,
  });

  return response.data;
}

/**
 * Clear cart at store or seller level.
 * @param {CartCheckout} cartInfo  object with buyerId and sellerId
 */
export async function deleteCart(cartInfo: CartCheckout) {
  const { buyerId, sellerId } = cartInfo;
  const params = {
    buyer_id: buyerId,
    seller_id: sellerId,
  };
  const response = await session.post(`${URLS.CARTS.CLEAR}/`, null, { params });

  return response.data;
}

export async function updateItem({ id, quantity }: CartItem): Promise<UpdateCartItemResponse> {
  const params: UpdateCartItemRequest = { quantity };

  const response = await session.patch(`${URLS.CARTS.ITEMS}/${id}/`, params);

  return response.data;
}

/**
 * Add a single item to the cart
 * @param {CartItem} cartItem
 */
export async function addToCart(cartItem: CartItem) {
  const params = { error_format: 'jsonapi' };

  const response = await session.post(`/${URLS.CARTS.ITEMS}/`, cartItem, { params });

  return response.data;
}

/**
 * Bulk add to cart
 * @param {CartItem[]} cartItems
 */
export async function bulkAddToCart(cartItems: CartItem[]) {
  const params = { error_format: 'jsonapi', bulk: true };

  const response = await session.post<BulkAddCartItemsResponse>(`/${URLS.CARTS.ITEMS}/`, cartItems, { params });

  return response.data;
}

export async function deleteItem(args: DeleteCartItemRequest) {
  const response = await session.delete(`${URLS.CARTS.ITEMS}/${args.id}/`);

  return response.data;
}

export async function validatePromoCode(code: string, buyerId: number, brandId: number, orderId: string) {
  const response = await session.get<PromoCode>(`${URLS.CART.VALIDATE_PROMOCODE}/`, {
    params: {
      code,
      buyer_id: buyerId,
      brand_id: brandId,
      order_id: orderId,
    },
  });

  return response.data;
}

/**
 * PATCH cart notes and delivery preferences
 */
export async function updateCartOrderDetails(args: UpdateCartOrderDetailsRequest) {
  const response = await session.patch(`${URLS.CARTS.DEFAULT}/${args.orderId}/`, args);

  return response.data;
}
